/**
 * Promote knowledge: one source's whole knowledge pipeline in a drawer, opened from the source. Generate the
 * questions and answers the source holds, check and edit each until it passes, promote it to serve as a
 * draft, and watch it earn auto-send. Everything here belongs to this source; the bar of four steps says how
 * far it is and opens the first item that needs work.
 */

import { Fragment, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronRight, Circle, FileText, Loader2, RefreshCw } from 'lucide-react';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
	useGenerateKnowledge,
	useKnowledgeArticles,
	useKnowledgeItems,
	useKnowledgeJobs,
	useSelectedBuId
} from '@/hooks/use-knowledge-promotion';
import { usePermissions } from '@/hooks/use-permissions';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { KnowledgeItemPanel } from './item-panel';
import { JobBanner } from './job-banner';
import { SOURCE_GROUPS, sourceSteps, type SourceStep } from './source-steps';
import { regenerateLossOf, stageBadge, stageOf, streakText } from './status';
import type { KnowledgeItem } from './types';

const STATE_ICON = {
	done: <CheckCircle2 className='size-3.5 shrink-0 text-green-600' aria-label='done' />,
	attention: <AlertCircle className='size-3.5 shrink-0 text-amber-600' aria-label='needs attention' />,
	todo: <Circle className='size-3.5 shrink-0 text-gray-500' aria-label='not reached' />
} as const;

const StepBar = ({ steps, onStep }: { steps: SourceStep[]; onStep: (step: SourceStep) => void }) => (
	<nav aria-label='Knowledge steps' className='flex items-stretch gap-1 overflow-x-auto'>
		{steps.map((step, index) => (
			<Fragment key={step.id}>
				{index > 0 && <ChevronRight className='size-3.5 shrink-0 self-center text-gray-500' aria-hidden />}
				<button
					type='button'
					onClick={() => onStep(step)}
					className={cn(
						'flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors hover:bg-gray-100',
						step.state === 'attention' ? 'border-amber-300 bg-muted/30' : 'border-gray-400 bg-muted/30'
					)}
				>
					{STATE_ICON[step.state]}
					<span className='flex min-w-0 flex-col'>
						<span className='text-[11px] leading-4 font-medium text-gray-900'>
							{index + 1}. {step.label}
						</span>
						<span className='truncate text-[11px] leading-4 text-gray-800'>{step.detail}</span>
					</span>
				</button>
			</Fragment>
		))}
	</nav>
);

interface RegenerateNoticeProps {
	sourceName: string;
	items: KnowledgeItem[];
	canEdit: boolean;
	disabled: boolean;
	onRegenerate: () => void;
}

/** Its answers were checked against the old text: a small change is an edit, a big one a fresh set. */
const RegenerateNotice = ({ sourceName, items, canEdit, disabled, onRegenerate }: RegenerateNoticeProps) => {
	const [confirming, setConfirming] = useState(false);
	return (
		<div
			className='flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2'
			data-testid='source-changed'
		>
			<p className='flex items-start gap-1.5 text-xs text-amber-900'>
				<AlertTriangle className='mt-0.5 size-3.5 shrink-0' aria-hidden />
				<span>
					This source changed since its answers were checked. Small change: edit the answers it affects and run Check.
					Big change: regenerate its questions and answers.
				</span>
			</p>
			{canEdit && (
				<Button size='sm' variant='outline' disabled={disabled} onClick={() => setConfirming(true)}>
					<RefreshCw className='size-3.5' aria-hidden />
					Regenerate
				</Button>
			)}
			<AlertDialog open={confirming} onOpenChange={setConfirming}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Regenerate from "{sourceName}"?</AlertDialogTitle>
						<AlertDialogDescription>
							This removes its {regenerateLossOf(items)} and generates new ones. New ones must pass Check and be
							promoted again.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={onRegenerate}>Regenerate</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};

interface BodyProps {
	sourceId: string;
	sourceName: string;
}

const SourceKnowledgeBody = ({ sourceId, sourceName }: BodyProps) => {
	const { can } = usePermissions();
	const canEdit = can('edit', 'promoted-blueprint');
	const buId = useSelectedBuId();
	const { data: allItems, isLoading } = useKnowledgeItems();
	const { data: articles, isLoading: articlesLoading } = useKnowledgeArticles();
	const { data: jobs } = useKnowledgeJobs();
	const generate = useGenerateKnowledge();
	const [openId, setOpenId] = useState<string | null>(null);

	const items = useMemo(() => (allItems ?? []).filter((item) => item.article?.id === sourceId), [allItems, sourceId]);
	// Only a source with text to read can be generated from: an article, or another source once indexed.
	const hasText = (articles ?? []).some((article) => article.id === sourceId);
	const generating = (jobs ?? []).some((job) => job.articleId === sourceId && job.status === 'running');
	const steps = sourceSteps(items, hasText);
	const open = items.find((item) => item.id === openId) ?? null;
	const sourceChanged = items.some((item) => item.article?.changed);

	const run = async (replace = false) => {
		try {
			await generate.mutateAsync(replace ? { articleId: sourceId, buId, replace } : { articleId: sourceId, buId });
			toast.success(
				replace
					? 'Regenerating its questions and answers. The new ones appear here as they are made.'
					: 'Generating its questions and answers. New ones appear here as they are made.'
			);
		} catch (error) {
			toast.error(getApiErrorMessage(error, replace ? 'Could not start regenerating' : 'Could not start generating'));
		}
	};

	return (
		<div className='flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4'>
			{/* A step opens the item its work is on; generating starts only from its own button. */}
			<StepBar steps={steps} onStep={(step) => step.itemId && setOpenId(step.itemId)} />
			<JobBanner articleId={sourceId} />
			{sourceChanged && (
				<RegenerateNotice
					sourceName={sourceName}
					items={items}
					canEdit={canEdit}
					disabled={!hasText || generating || generate.isPending}
					onRegenerate={() => void run(true)}
				/>
			)}

			<div className='flex min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-500'>
				<div className='flex min-h-0 min-w-0 flex-1 flex-col'>
					<div className='flex shrink-0 items-center justify-between gap-2 border-b border-gray-500 px-3 py-2'>
						<p className='text-xs text-gray-800'>
							{items.length
								? `${items.length} question${items.length === 1 ? '' : 's'} from this source`
								: 'No questions yet'}
						</p>
						{canEdit && (
							<Button size='sm' disabled={!hasText || generating || generate.isPending} onClick={() => void run()}>
								{generating || generate.isPending ? (
									<Loader2 className='size-3.5 animate-spin' />
								) : (
									<FileText className='size-3.5' />
								)}
								{items.length ? 'Generate more' : 'Generate questions and answers'}
							</Button>
						)}
					</div>
					<div className='min-h-0 flex-1 overflow-y-auto' data-testid='source-knowledge-items'>
						{isLoading || articlesLoading ? (
							<div className='flex flex-col gap-2 p-3'>
								<Skeleton className='h-10 w-full' />
								<Skeleton className='h-10 w-full' />
							</div>
						) : items.length === 0 ? (
							<p className='p-4 text-sm text-muted-foreground'>
								{hasText
									? 'Generate the questions this source answers. Each becomes an answer you check, edit and promote.'
									: 'This source has no indexed text yet. Once it is indexed, its questions and answers can be generated here.'}
							</p>
						) : (
							SOURCE_GROUPS.map((group) => {
								const rows = items.filter((item) => group.stages.includes(stageOf(item)));
								if (!rows.length) return null;
								return (
									<section key={group.label} aria-label={group.label}>
										<p className='bg-muted/40 px-3 py-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase'>
											{group.label} · {rows.length}
										</p>
										{rows.map((item) => {
											const badge = stageBadge(item);
											const streak = streakText(item);
											return (
												<button
													key={item.id}
													type='button'
													data-testid='source-knowledge-row'
													onClick={() => setOpenId(item.id)}
													aria-current={openId === item.id}
													className={cn(
														'flex w-full items-start justify-between gap-3 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted/50',
														openId === item.id && 'bg-muted'
													)}
												>
													<span className='flex min-w-0 flex-col gap-0.5'>
														<span className='truncate text-[13px] font-medium'>{item.question || item.intent}</span>
														<span className='truncate text-[11px] text-muted-foreground'>{item.intent}</span>
													</span>
													<span className='flex shrink-0 flex-col items-end gap-0.5'>
														<Badge variant={badge.variant} className='text-[10px]'>
															{badge.label}
														</Badge>
														{streak && (
															<span className='text-[10px] text-muted-foreground'>{streak} sent unchanged</span>
														)}
													</span>
												</button>
											);
										})}
									</section>
								);
							})
						)}
					</div>
				</div>
				{open ? (
					<KnowledgeItemPanel key={open.id} item={open} canEdit={canEdit} onClose={() => setOpenId(null)} />
				) : (
					items.length > 0 && (
						<p className='w-[520px] shrink-0 border-l p-4 text-xs text-muted-foreground'>
							Open a question to check it, edit its answer, promote it and see how close it is to auto-send.
						</p>
					)
				)}
			</div>
		</div>
	);
};

interface SourceKnowledgeSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	sourceId: string;
	sourceName: string;
}

export const SourceKnowledgeSheet = ({ open, onOpenChange, sourceId, sourceName }: SourceKnowledgeSheetProps) => (
	<Sheet open={open} onOpenChange={onOpenChange}>
		<SheetContent
			side='right'
			className='flex w-[92vw] flex-col gap-3 bg-background p-0 backdrop-blur-none sm:max-w-none'
		>
			<SheetHeader className='px-4 pt-4 pb-0'>
				<SheetTitle>Promote knowledge</SheetTitle>
				<SheetDescription>
					{sourceName}: generate the questions it answers, check and edit each, promote them to serve as drafts, and
					they earn auto-send once agents send their drafts unchanged.
				</SheetDescription>
			</SheetHeader>
			{open && <SourceKnowledgeBody sourceId={sourceId} sourceName={sourceName} />}
		</SheetContent>
	</Sheet>
);
